// src/features/guias/GuiasTab.jsx
import React, { useState, useEffect } from "react";
import { useGuias } from "./hooks/useGuias";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "components/ui/table";
import { Search, Map, Edit, Trash2 } from "lucide-react";
import EditGuiaModal from "./components/EditGuiaModal";

function GuiasTab() {
  const { guias, isLoading, fetchGuias, deleteGuia, addGuia } = useGuias();
  const [searchTerm, setSearchTerm] = useState("");
  const [editingGuia, setEditingGuia] = useState(null);

  useEffect(() => {
    fetchGuias();
  }, [fetchGuias]);

  const filteredGuias = guias.filter(
    (guia) =>
      guia.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guia.phone.includes(searchTerm)
  );

  const handleUpdateGuia = async (data) => {
    const success = await addGuia(data);
    if (success) {
      fetchGuias();
      setEditingGuia(null);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Map className="h-6 w-6 text-green-600 dark:text-muted-foreground" />
          Guias de Turismo
          {!isLoading && (
            <span className="ml-2 text-sm font-normal text-muted-foreground bg-gray-100 px-2.5 py-0.5 rounded-full dark:bg-gray-800 border">
              {guias.length}
            </span>
          )}
        </h2>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome ou telefone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        {searchTerm && (
          <span className="text-sm text-muted-foreground">
            Encontrados: {filteredGuias.length}
          </span>
        )}
      </div>

      {/* Visão Mobile (Cards) */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <div className="text-center py-8 bg-white dark:bg-card rounded-lg border dark:border-border text-muted-foreground">
            Carregando guias...
          </div>
        ) : filteredGuias.length === 0 ? (
          <div className="text-center py-8 bg-white dark:bg-card rounded-lg border dark:border-border text-muted-foreground">
            Nenhum guia encontrado.
          </div>
        ) : (
          filteredGuias.map((guia) => (
            <div key={guia.id} className="bg-white dark:bg-card rounded-lg border dark:border-border p-4 shadow-sm flex flex-col gap-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg text-green-700 dark:text-muted-foreground">{guia.name}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{guia.phone || "Sem telefone"}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t dark:border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingGuia(guia)}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" /> Editar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteGuia(guia.id)}
                  className="flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Excluir
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Visão Desktop (Tabela) */}
      <div className="hidden md:block rounded-md border bg-white dark:bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  Carregando guias...
                </TableCell>
              </TableRow>
            ) : filteredGuias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  Nenhum guia encontrado.
                </TableCell>
              </TableRow>
            ) : (
              filteredGuias.map((guia) => (
                <TableRow key={guia.id}>
                  <TableCell className="font-medium text-green-700 dark:text-muted-foreground">{guia.name}</TableCell>
                  <TableCell>{guia.phone || "-"}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingGuia(guia)}
                      title="Editar Guia"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteGuia(guia.id)}
                      title="Excluir Guia"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <EditGuiaModal
        isOpen={!!editingGuia}
        onClose={() => setEditingGuia(null)}
        onSave={handleUpdateGuia}
        guiaData={editingGuia}
      />
    </div>
  );
}

export default GuiasTab;
